<template>
    <section class="main">
        <div>
            <div class="grid-container">
                <div class="action action--page">
                    <router-link
                        :to="{ name : 'mixingplants' }"
                        class="button button--prev"
                        :title="$t('prev_page')"
                        tabindex="-1"
                        >
                            <button>{{ $t('prev_page') }}</button>
                        </router-link>

                    <h1>{{ $t('mixingplants.title.search') }}</h1>
                </div>
            </div>
        </div>

        <div class="grid-container">
            <div class="grid-x">
                <div class="cell medium-9 medium-offset-1 large-11 large-offset-0">
                    <v-action-buttons :items="getActionButtons" />

                    <div class="box">
                        <form
                            action
                            method="POST"
                            class="filter filter--search"
                            novalidation
                            @submit.prevent="onFilterMixingplants"
                        >
                            <div class="filter__grid">
                                <div class="filter__grid__item">
                                    <div class="form-group">
                                        <label for="form-field-city">{{ $t('form.city') }}</label>
                                        <input
                                            type="text"
                                            class="form-control form-control--small form-control--search"
                                            :placeholder="$t('form.city')"
                                            v-model="mixingplantTableFilter.city"
                                        />
                                    </div>
                                </div>
                                <div class="filter__grid__item filter__grid__item--normal">
                                    <div class="form-group">
                                        <label for="form-field-zipcode">{{ $t('form.zipcode') }}</label>
                                        <input
                                            type="text"
                                            class="form-control form-control--small form-control--search"
                                            :placeholder="$t('form.zipcode')"
                                            v-model="mixingplantTableFilter.zipcode"
                                        />
                                    </div>
                                </div>
                                <div class="filter__grid__item filter__grid__item--medium">
                                    <div class="form-group">
                                        <label for="form-field-country">{{ $t('customers.country') }}</label>
                                        <v-select-country
                                            id="form-field-country"
                                            v-model="mixingplantTableFilter.countryCode"
                                            :dachCountriesOnly="true"
                                        />
                                    </div>
                                </div>
                                <div class="filter__grid__item filter__grid__item--medium">
                                    <div class="form-group">
                                        <label for="form-field-distance">{{ $t('form.distance.km') }}</label>
                                        <input
                                            type="text"
                                            class="form-control form-control--small form-control--search"
                                            :placeholder="$t('form.distance.km')"
                                            v-model="mixingplantTableFilter.distanceInKm"
                                        />
                                    </div>
                                </div>
                                <div class="filter__grid__item filter__grid__item--normal">
                                    <div class="form-group form-group--row u-no-margin--bottom">
                                        <div class="form-group">
                                            <button
                                                role="button"
                                                type="submit"
                                                class="button u-extra-small-margin--top"
                                                :title="$t('search')"
                                            >
                                                {{ $t("search") }}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>

                        <div class="form-block">
                            <div class="form-group">
                                <div class="form-control form-control--maps">
                                    <v-google-map-items
                                        :key="renderCounter"
                                        :items="resultsTableItems"
                                        :selectedItem="selectedMixingplant"
                                    />
                                </div>
                            </div>
                        </div>

                        <v-table
                            :isLoading="resultsTableLoading"
                            :columns="resultsTableColumns"
                            :items="resultsTableItems"
                            :showHeaderRow="true"
                            :showShowAll="false"
                            :useDetailRow="false"
                        >
                            <template slot="name" slot-scope="{ row }">
                                <div class="l-ranking-grid">
                                    <div class="l-ranking-grid__cell">
                                       <span :class="'c-ranking c-ranking--' + (row.ranking || 'none') "></span>
                                    </div>
                                     <div class="l-ranking-grid__cell">
                                        <span class="table-description"> {{ row.name }}</span>
                                    </div>
                                </div>
                            </template>

                            <template slot="distance" slot-scope="{ row }">
                                <span v-if="row.distance" class="table-description table-description--normal">
                                    {{ Number(row.distance).toFixed(2) }} {{ $t('unit.km') }}
                                </span>
                                <span v-else class="table-description table-description--normal">
                                    -
                                </span>
                            </template>

                            <template slot="address" slot-scope="{ row }">
                                <span class="table-description table-description--normal">
                                    {{ row.address.street }} {{ row.address.housenumber }}
                                </span>
                            </template>

                            <template slot="city" slot-scope="{ row }">
                                <span class="table-description table-description--normal">{{ row.address.city }}</span>
                            </template>

                            <template slot="zipcode" slot-scope="{ row }">
                                <span class="table-description table-description--normal">{{ row.address.zipcode }}</span>
                            </template>

                            <template slot="telephone" slot-scope="{ row }">
                                <span class="table-description table-description--normal">
                                    <a :href="`tel:${row.phone}`">{{ row.phone }}</a>
                                </span>
                            </template>

                            <template slot="email" slot-scope="{ row }">
                                <span class="table-description table-description--normal">
                                    <a :href="`mailto:${row.email}`">{{ row.email }}</a>
                                </span>
                            </template>

                            <template slot="marker" slot-scope="{ row }">
                                <button @click="highlightMarkerOnMapFor(row)">
                                    <img :src="pinUrl" width="14" />
                                </button>
                            </template>

                            <template slot="link" slot-scope="{ row }">
                                 <router-link
                                    :to="{ name: 'update-mixingplant', params: { mixingplant: row.id }, query: { source: 'search', ...mixingplantTableFilter } }"
                                    class="button button--action button--action--edit button--small"
                                    :title="$t('update')"
                                    >{{ $t('update') }}</router-link>
                            </template>
                        </v-table>

                        <v-ranking-legend></v-ranking-legend>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import _ from "lodash";
import PageHeader from "./../partials/PageHeader.vue";
import PageLoading from "./../partials/PageLoading.vue";
import ActionButtons from "./../partials/Foundation/ActionButtons.vue";
import Table from "./../partials/Foundation/Table.vue";
import RankingLegend from "./../partials/Rankinglegend.vue";
import SelectCountry from './../partials/Forms/SelectCountry.vue';
import GoogleMapItems from './../partials/GoogleMapItems.vue';

export default {
    name: "Mixingplants",
    data() {
        return {
            renderCounter: 0,
            selectedMixingplant: null,
            resultsTableLoading: false,
            resultsTableItems: [],
            mixingplantTableFilter: {
                city: this.$route.query.city,
                zipcode: this.$route.query.zipcode,
                countryCode: this.$route.query.countryCode || 'de',
                distanceInKm: this.$route.query.distanceInKm || 30
            },
            pinUrl: "/assets/img/leaflet/marker-icon-red.svg",
        };
    },

    components: {
        vPageHeader: PageHeader,
        vPageLoading: PageLoading,
        vActionButtons: ActionButtons,
        vTable: Table,
        vRankingLegend: RankingLegend,
        vSelectCountry: SelectCountry,
        vGoogleMapItems: GoogleMapItems,
    },

    computed: {
        resultsTableColumns () {
            return [
                { key: "name", label: this.$t('companyname') },
                { key: "distance", label: "Entfernung" },
                { key: "address", label: this.$t('form.account.street') },
                { key: "zipcode", label: this.$t('form.zipcode') },
                { key: "city", label: this.$t('form.account.city') },
                { key: "telephone", label: this.$t('phone') },
                { key: "link", label: "" },
            ];
        },

        getActionButtons () {
            const buttons = [];

            if (this.$store.getters.userHasPermission("mixingplant_list")) {
                buttons.push({
                    action: "edit",
                    title: this.$t('mixingplants.list.button.title'),
                    router: { name: "mixingplants" },
                });
            }

            return buttons;
        },
    },

    created () {
        this.updateResultsTableItems();
    },

    methods: {
        /**
         * Get our mixing plants
         */
        updateResultsTableItems () {
            this.resultsTableLoading = true;

            const filterHaveChanged = !_.isEqual(this.$route.query, this.mixingplantTableFilter);

            if (filterHaveChanged) {
                this.$router.replace({ name: 'search-mixingplants', query: this.mixingplantTableFilter })
            }

            this.$api
                .clearParams()
                .addParam('sortby', 'name')
                .addParam('sortdir', 'asc')
                .addParam('city', this.mixingplantTableFilter.city)
                // easter egg: '87600' is the Asphaltbau HQ zipcode
                .addParam('zipcode', this.mixingplantTableFilter.zipcode ?? '87600')
                .addParam('countryCode', this.mixingplantTableFilter.countryCode)
                .addParam('distanceInKm', this.mixingplantTableFilter.distanceInKm)
                .get('/mixingplants/search')
                .then(response => {
                    // console.log('results --> ', response.data)

                    // we’re assigning an empty zipcode here to not default to the Asphaltbau HQ zipcode in the next query
                    this.mixingplantTableFilter.zipcode = this.mixingplantTableFilter.zipcode ?? ''

                    this.renderCounter++
                    this.resultsTableItems = response.data.data;
                    this.resultsTableLoading = false;
                }).catch(error => {
                    this.resultsTableLoading = false;
                    this.resultsTableItems = [];

                    console.error('Failted to filter mixing plants', error)
                    // TODO: show error message to user?
                });
        },

        onFilterMixingplants () {
            this.updateResultsTableItems();
        },

        /**
         * @param {{ id: string, title: string}} options
         */
        findCountryByCountryCode ({ id }) {
            this.mixingplantTableFilter.countryCode = id
        },

        highlightMarkerOnMapFor (selectedMixingplant) {
            this.selectedMixingplant = selectedMixingplant
        },
    }
 }

</script>
